import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Loader2, AlertCircle, SlidersHorizontal, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import { Navigate } from 'react-router-dom';
import { apiRequest } from '../services/apiClient';
import { fmtDateTime } from '../utils/format';
import Modal from '../components/Modal';

const ROLES = [
  { value: 'user',       label: 'User',       desc: 'Read-only. Can submit TPRM requests.' },
  { value: 'data',       label: 'Data',       desc: 'Read-only, incl. restricted (reporting/export).' },
  { value: 'finance',    label: 'Finance',    desc: 'Read-only, incl. restricted (financial).' },
  { value: 'operations', label: 'Operations', desc: 'Read-only, incl. restricted (operational).' },
  { value: 'sales',       label: 'Sales',          desc: 'Read-only (excludes restricted contracts).' },
  { value: 'engineering', label: 'Engineering',    desc: 'Read-only (excludes restricted contracts).' },
  { value: 'product',     label: 'Product',        desc: 'Read-only (excludes restricted contracts).' },
  { value: 'hr',          label: 'Human Resources', desc: 'Read-only (excludes restricted contracts).' },
  { value: 'security',   label: 'Security',   desc: 'Read + review TPRM requests.' },
  { value: 'compliance', label: 'Compliance', desc: 'Compliance reviewer (capabilities being scoped).' },
  { value: 'legal',      label: 'Legal',      desc: 'Full write access to contracts.' },
  { value: 'admin',      label: 'Admin',      desc: 'All of legal + manages user roles + Drive config.' },
];

const ROLE_CHIP = {
  user:       'chip gray',
  data:       'chip sky',
  finance:    'chip red',
  operations: 'chip neutral',
  sales:       'chip gray',
  engineering: 'chip gray',
  product:     'chip gray',
  hr:          'chip gray',
  security:   'chip blue',
  compliance: 'chip purple',
  legal:      'chip green',
  admin:      'chip yellow',
};

// Capabilities an admin can grant to an individual user on top of their role
// (e.g. an Operations COO who also needs to see restricted contracts). Must
// match the backend GRANTABLE_CAPABILITIES set.
export const GRANTABLE = [
  { value: 'see_restricted',    label: 'Restricted',         desc: 'See restricted (internal-only) contracts.' },
  { value: 'upload_contracts',  label: 'Upload',             desc: 'Upload agreements — each is AI-scanned into a contract that legal then reviews.' },
  { value: 'review_legal',      label: 'Legal review',       desc: 'Review TPRM requests in the legal lane.' },
  { value: 'review_security',   label: 'Security review',    desc: 'Review TPRM requests in the security lane.' },
  { value: 'review_compliance', label: 'Compliance review',  desc: 'Review TPRM requests in the compliance lane.' },
  { value: 'manage_intakes',    label: 'Intakes',            desc: 'View and manage all self-serve intakes.' },
  { value: 'manage_schemas',    label: 'Schemas',            desc: 'Create and edit structured-data schemas.' },
  { value: 'run_schemas',       label: 'Run extractions',    desc: 'Run and retry schema extractions on accessible contracts; no contract editing or verification.' },
  { value: 'view_conversations', label: 'Conversations',     desc: 'View assistant conversation transcripts.' },
];

const GRANT_LABEL = Object.fromEntries(GRANTABLE.map((g) => [g.value, g.label]));

function Avatar({ user }) {
  if (user.avatarUrl) {
    return <img src={user.avatarUrl} alt="" className="avatar" />;
  }
  const initial = (user.name || user.email || '?').charAt(0).toUpperCase();
  return (
    <div
      className="avatar"
      style={{
        background: 'var(--surface-alpha-tertiary)',
        color: 'var(--text-secondary)',
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {initial}
    </div>
  );
}

export default function AdminUsers() {
  const { user: currentUser, can, loading: authLoading, startViewAs } = useAuth();
  const canManageRoles = can('manage_roles');
  const { showToast } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Which user's "manage access" modal is open. Derive the user from `users` so
  // toggles inside the modal reflect the latest grants without extra state.
  const [accessUserId, setAccessUserId] = useState(null);
  const accessUser = users.find((u) => u.id === accessUserId) || null;

  const loadUsers = useCallback(() => {
    setLoading(true);
    apiRequest('/admin/users')
      .then((data) => {
        setUsers(data.users || []);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (canManageRoles) loadUsers();
  }, [canManageRoles, loadUsers]);

  const handleRoleChange = async (user, newRole) => {
    if (newRole === user.role) return;
    setSavingId(user.id);
    try {
      const updated = await apiRequest(`/admin/users/${user.id}/role`, {
        method: 'PUT',
        json: { role: newRole },
      });
      setUsers((prev) => prev.map((u) => (u.id === user.id
        ? { ...u, role: updated.role, roleCapabilities: updated.roleCapabilities, extraCapabilities: updated.extraCapabilities }
        : u)));
      showToast(`${user.email} is now ${updated.role}`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingId(null);
    }
  };

  // Per-user grant on top of the role. Toggles a single capability and saves.
  const handleGrantToggle = async (user, cap) => {
    const current = user.extraCapabilities || [];
    const next = current.includes(cap) ? current.filter((c) => c !== cap) : [...current, cap];
    setSavingId(user.id);
    try {
      const updated = await apiRequest(`/admin/users/${user.id}/capabilities`, {
        method: 'PUT',
        json: { capabilities: next },
      });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, extraCapabilities: updated.extraCapabilities } : u)));
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSavingId(null);
    }
  };

  // View as user (doc-323): render the whole app through this person's
  // effective permissions — role capabilities plus their per-user grants —
  // read-only and enforced server-side, so it's a lens and not an escalation.
  const handleViewAs = async (user) => {
    setSavingId(user.id);
    try {
      await startViewAs(user.id);
    } catch (err) {
      showToast(err.message, 'error');
      setSavingId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }

  if (!canManageRoles) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">User roles</h1>
          <div className="page-subtitle">
            Set each person's role, and grant extra read/review access to individuals on top of it
            (e.g. an Operations COO who also needs to see restricted contracts).
          </div>
        </div>
      </div>

      {/* Role legend */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 10,
          marginBottom: 20,
        }}
      >
        {ROLES.map((r) => (
          <div
            key={r.value}
            className="card"
            style={{
              padding: '10px 12px',
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <span className={ROLE_CHIP[r.value]}>{r.label}</span>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, flex: 1 }}>
              {r.desc}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <div className="callout red" style={{ marginBottom: 16 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body" style={{ fontSize: 12.5 }}>{error}</div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
        </div>
      ) : (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ paddingLeft: 24 }}>User</th>
                <th>Email</th>
                <th>Last login</th>
                <th style={{ width: 160 }}>Role</th>
                <th style={{ width: 230 }}>Extra access</th>
                <th style={{ width: 100, paddingRight: 24 }}>Preview</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                const isSaving = savingId === u.id;
                return (
                  <tr key={u.id} style={{ cursor: 'default' }}>
                    <td style={{ paddingLeft: 24 }}>
                      <div className="row" style={{ gap: 10 }}>
                        <Avatar user={u} />
                        <div style={{ fontSize: 13, fontWeight: 450, color: 'var(--text-primary)' }}>
                          {u.name || '—'}
                          {isSelf && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>
                              (you)
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                      {fmtDateTime(u.lastLogin)}
                    </td>
                    {/* Role — just the selector. */}
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <select
                          value={u.role || 'user'}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={isSaving}
                          className="select"
                          style={{ flex: 1 }}
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                        {isSaving && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
                        )}
                      </div>
                    </td>
                    {/* Extra access — its own column: active grants + a Manage
                        button that opens the editor. Keeps each row to one line. */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {(u.extraCapabilities || []).length > 0 ? (
                          (u.extraCapabilities || []).map((cap) => (
                            <span key={cap} className="chip green" style={{ fontSize: 10 }} title="Granted on top of role">
                              +{GRANT_LABEL[cap] || cap}
                            </span>
                          ))
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>None</span>
                        )}
                        <button
                          type="button"
                          className="btn ghost xs"
                          onClick={() => setAccessUserId(u.id)}
                          disabled={isSaving}
                          title="Manage extra access"
                        >
                          <SlidersHorizontal className="w-3 h-3" />
                          Manage
                        </button>
                      </div>
                    </td>
                    {/* View as — the whole app through this person's effective
                        permissions, read-only. Pointless on yourself. */}
                    <td style={{ paddingRight: 24 }}>
                      {!isSelf && (
                        <button
                          type="button"
                          className="btn ghost xs"
                          onClick={() => handleViewAs(u)}
                          disabled={isSaving}
                          title={`See Docket as ${u.name || u.email} sees it — read-only`}
                        >
                          <Eye className="w-3 h-3" />
                          View as
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      padding: '48px 24px',
                      textAlign: 'center',
                      fontSize: 12.5,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No users yet. Users appear here after their first Google sign-in.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div
        className="row"
        style={{
          marginTop: 16,
          gap: 6,
          fontSize: 11,
          color: 'var(--text-tertiary)',
        }}
      >
        <Shield className="w-3 h-3" />
        You can't demote yourself from admin — ask another admin to do it.
      </div>

      <Modal
        isOpen={!!accessUser}
        onClose={() => setAccessUserId(null)}
        title={`Extra access — ${accessUser?.name || accessUser?.email || ''}`}
        description="Capabilities granted on top of this person's role."
        icon={SlidersHorizontal}
        size="sm"
        footer={<button type="button" className="btn primary sm" onClick={() => setAccessUserId(null)}>Done</button>}
      >
        <div className="card-body" style={{ paddingTop: 4 }}>
          {accessUser && GRANTABLE.map((g) => {
            const viaRole = (accessUser.roleCapabilities || []).includes(g.value);
            const granted = (accessUser.extraCapabilities || []).includes(g.value);
            const saving = savingId === accessUser.id;
            return (
              <label
                key={g.value}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 0',
                  borderTop: '0.5px solid var(--border-primary)',
                  cursor: viaRole ? 'default' : 'pointer',
                  opacity: viaRole ? 0.6 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={viaRole || granted}
                  disabled={viaRole || saving}
                  onChange={() => handleGrantToggle(accessUser, g.value)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{g.label}</span>
                  {viaRole && (
                    <span className="muted" style={{ fontSize: 11 }}> · already included by the {accessUser.role} role</span>
                  )}
                  <div className="muted" style={{ fontSize: 12, marginTop: 1 }}>{g.desc}</div>
                </span>
              </label>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
